/**
 * Where to land after sign-in. ProtectedRoute puts the page the user asked
 * for (e.g. /?fileId=<id> from Drive's "Open in PDF editor") into the OIDC `state`;
 * the callback reads it back. Only same-origin paths are honoured, so a
 * crafted state can never bounce the user to another site.
 */
const STORAGE_KEY = "fg.auth.returnTo";

/** The current page as a path (+ query + hash). */
export function currentPath(): string {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

/** `value` when it is a path on this origin (and not the auth pages themselves), else null. */
export function safeReturnPath(value: unknown): string | null {
    if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) {
        return null;
    }
    try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin || url.pathname.startsWith("/auth/")) return null;
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
}

/** OIDC `state` for signinRedirect — carries the page to come back to. */
export function returnState(): { returnTo: string } | undefined {
    const path = safeReturnPath(currentPath());
    return path ? { returnTo: path } : undefined;
}

/** Called from onSigninCallback with the signed-in user's `state`. */
export function rememberReturnTo(state: unknown): void {
    const path = safeReturnPath((state as { returnTo?: unknown } | null | undefined)?.returnTo);
    try {
        if (path) window.sessionStorage.setItem(STORAGE_KEY, path);
        else window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        /* storage disabled — the callback falls back to the user's state */
    }
}

/** The remembered path (once), or `fallback`. */
export function consumeReturnTo(state: unknown, fallback = "/"): string {
    let stored: string | null = null;
    try {
        stored = window.sessionStorage.getItem(STORAGE_KEY);
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        /* storage disabled */
    }
    return (
        safeReturnPath(stored) ??
        safeReturnPath((state as { returnTo?: unknown } | null | undefined)?.returnTo) ??
        fallback
    );
}
